import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Paper,
  Grid,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  Alert,
  Dialog,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Menu,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Header from '../components/Header';
import RichTextEditor from '../components/RichTextEditor';
import ExpenseItemsTable, { ExpenseItem as ExpenseItemType } from '../components/ExpenseItemsTable';
import ByCategoryExpenses from './components/ByCategoryExpenses';
import IndividualExpenses from './components/IndividualExpenses';
import { downloadExpensesSpreadsheet, getExpenseTotalAmount } from './components/expenseUtils';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';
import { FileArtifact } from '../types';
import { useAuth } from '../context/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ApprovedItemRequest {
  documentId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  item_estimate?: {
    item: string;
    totalCost: number;
  };
  aproval_request?: {
    title: string;
  };
}

interface ExpenseItem {
  id: string;
  title: string;
  expenseItems: ExpenseItemType[];
  totalAmount: number;
  isExceptionRequest: boolean;
  sendExceptionApprovalTo?: string;
  approvedItem?: ApprovedItemRequest;
  attachments?: FileArtifact[];
  createdAt: string;
  remianingAmount?: number;
  comment?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`expense-tabpanel-${index}`}
      aria-labelledby={`expense-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ExpenseReport() {
  const { user, handleUnauthorized } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [approvedItems, setApprovedItems] = useState<ApprovedItemRequest[]>([]);
  const [submittedExpenses, setSubmittedExpenses] = useState<ExpenseItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: string }>({});
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [totalCommitmentPaid, setTotalCommitmentPaid] = useState<number>(0);
  const [commitmentLoading, setCommitmentLoading] = useState(false);

  // Nested tabs for Expense Details
  const [detailsTabValue, setDetailsTabValue] = useState(0);

  // Check if user has Procurement role
  const isProcurement = user?.role?.name === 'Procurement';

  // Spreadsheet download
  const [downloadingSpreadsheet, setDownloadingSpreadsheet] = useState(false);

  const handleDownloadExpensesSpreadsheet = () => {
    try {
      setDownloadingSpreadsheet(true);
      downloadExpensesSpreadsheet(submittedExpenses);
    } finally {
      setDownloadingSpreadsheet(false);
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    selectedApprovedItem: '',
    isExceptionRequest: 'No',
    exceptionApprovalTo: '',
    comment: '',
    unitPrice: '', // Deprecated - kept for backward compatibility
    totalPrice: '', // Deprecated - kept for backward compatibility
  });

  const [expenseItems, setExpenseItems] = useState<ExpenseItemType[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileArtifact[]>([]);
  const [budgetStatus, setBudgetStatus] = useState<'within' | 'threshold' | 'above' | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch approved item requests
  useEffect(() => {
    const fetchApprovedItems = async () => {
      try {
        const token = localStorage.getItem('strapiToken');
        // First try with published filter, then fallback to all items
        let endpoint = `${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.LIST}?pagination[limit]=1000&populate=*&filters[aproval_request][approvalStatus][$eq]=Approved&publishedAt[$ne]=null`;
        console.log('Fetching approved items from:', endpoint);
        
        let response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Fetch response status:', response.status);

        // If no published items found, try without the publishedAt filter
        if (response.ok) {
          const data = await response.json();
          console.log('Approved items data:', data);
          
          if (!data.data || data.data.length === 0) {
            console.log('No published items found, trying to fetch all items...');
            endpoint = `${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.LIST}?pagination[limit]=1000&populate=*&filters[aproval_request][approvalStatus][$eq]=Approved`;
            response = await fetch(endpoint, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (response.ok) {
              const dataAll = await response.json();
              console.log('All items data:', dataAll);
              setApprovedItems(dataAll.data || []);
            }
          } else {
            setApprovedItems(data.data || []);
          }
        } else {
          if (response.status === 401 || response.status === 403) {
            handleUnauthorized();
            return;
          }
          const errorData = await response.json();
          console.error('Failed to fetch approved items:', response.status, errorData);
        }
      } catch (error) {
        console.error('Failed to fetch approved items:', error);
      }
    };

    fetchApprovedItems();
  }, []);

  // Fetch all expenses
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setExpenseLoading(true);
        const token = localStorage.getItem('strapiToken');
        // Fetch expenses - explicitly populate attachment and nested ApprovedItem with its relations
        const endpoint = `${API_BASE_URL}${API_ENDPOINTS.EXPENSES.LIST}?pagination[limit]=1000&populate[0]=attachment&populate[1]=ApprovedItem&populate[2]=ApprovedItem.item_estimate&populate[3]=ApprovedItem.aproval_request`;
        console.log('Fetching expenses from:', endpoint);

        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Expenses data:', data);
          setSubmittedExpenses(data.data || []);
        } else {
          if (response.status === 401 || response.status === 403) {
            handleUnauthorized();
            return;
          }
          const errorData = await response.json();
          console.error('Failed to fetch expenses:', response.status, errorData);
        }
      } catch (error) {
        console.error('Failed to fetch expenses:', error);
      } finally {
        setExpenseLoading(false);
      }
    };

    fetchExpenses();
  }, []);

  // Fetch total commitment paid from all 3 commitment sources
  useEffect(() => {
    const fetchCommitmentPaid = async () => {
      setCommitmentLoading(true);
      try {
        const token = localStorage.getItem('strapiToken');
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const pageParams = 'pagination[pageSize]=5000&pagination[page]=1&fields[0]=paid';

        const [commRes, savasinRes, membersRes] = await Promise.all([
          fetch(`${API_BASE_URL}${API_ENDPOINTS.COMMITMENTS.LIST}?${pageParams}`, { headers }),
          fetch(`${API_BASE_URL}${API_ENDPOINTS.SAVASIN_DETAILS.LIST}?${pageParams}`, { headers }),
          fetch(`${API_BASE_URL}${API_ENDPOINTS.ALL_MEMBERS.LIST}?${pageParams}`, { headers }),
        ]);

        let total = 0;
        if (commRes.ok) {
          const d = await commRes.json();
          total += (d.data || []).reduce((s: number, r: any) => s + (r.paid || 0), 0);
        }
        if (savasinRes.ok) {
          const d = await savasinRes.json();
          total += (d.data || []).reduce((s: number, r: any) => s + (r.paid || 0), 0);
        }
        if (membersRes.ok) {
          const d = await membersRes.json();
          total += (d.data || []).reduce((s: number, r: any) => s + (r.paid || 0), 0);
        }

        setTotalCommitmentPaid(total);
      } catch (err) {
        console.error('Failed to fetch commitment paid totals:', err);
      } finally {
        setCommitmentLoading(false);
      }
    };
    fetchCommitmentPaid();
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset budget status when changing selected item
    if (name === 'selectedApprovedItem') {
      setBudgetStatus(null);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: FileArtifact[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      newFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        file: file,
      });
    }
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getGrandTotal = (): number => {
    return expenseItems.reduce((sum, item) => {
      const itemCost = item.total !== undefined ? item.total : item.rate * item.quantity;
      return sum + itemCost;
    }, 0);
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setSubmitError('Please enter expense title');
      return false;
    }
    if (expenseItems.length === 0) {
      setSubmitError('Please add at least one expense item');
      return false;
    }
    // Approved item is required only for regular expenses, not for exception requests
    if (formData.isExceptionRequest === 'No' && !formData.selectedApprovedItem) {
      setSubmitError('Please select an approved item');
      return false;
    }
    if (formData.isExceptionRequest === 'Yes' && !formData.exceptionApprovalTo) {
      setSubmitError('Please select exception approval recipient');
      return false;
    }
    // Exception request total amount must not exceed 10000
    if (formData.isExceptionRequest === 'Yes') {
      const totalAmount = getGrandTotal();
      if (totalAmount > 10000) {
        setSubmitError('Exception request total amount cannot exceed ₹10,000');
        return false;
      }
    }
    return true;
  };

  const handleSubmitExpense = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('strapiToken');
      if (!token) {
        setSubmitError('Authentication token not found');
        setIsSubmitting(false);
        return;
      }

      let uploadedFileIds: any[] = [];

      // Calculate total amount from expense items - use custom total if provided, otherwise rate * quantity
      const totalAmount = expenseItems.reduce((sum, item) => {
        const itemCost = item.total !== undefined ? item.total : item.rate * item.quantity;
        return sum + itemCost;
      }, 0);

      // Step 1: Create expense record first (WITHOUT ApprovedItem relation - will link after)
      const expenseData: any = {
        data: {
          title: formData.title,
          expenseItems: expenseItems,
          totalAmount: totalAmount,
          comment: formData.comment || null,
          isExceptionRequest: formData.isExceptionRequest === 'Yes',
          // Note: createdBy and updatedBy are set by backend lifecycle hooks
        }
      };

      console.log('Expense data to submit:', JSON.stringify(expenseData, null, 2));

      if (formData.isExceptionRequest === 'Yes') {
        expenseData.data.sendExceptionApprovalTo = formData.exceptionApprovalTo;
        expenseData.data.ExceptionApproval = {
          status: 'Pending',
          sentTo: formData.exceptionApprovalTo,
          sentAt: new Date().toISOString()
        };
      }

      const expenseResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EXPENSES.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(expenseData)
      });

      if (!expenseResponse.ok) {
        const errorData = await expenseResponse.json();
        console.error('Backend error response:', errorData);
        throw new Error(errorData.error?.message || 'Failed to create expense');
      }

      const createdExpense = await expenseResponse.json();
      const expenseId = createdExpense.data.documentId;
      console.log('Created expense (draft):', expenseId);

      // Step 1.5: Link ApprovedItem relation (right after expense creation)
      if (formData.selectedApprovedItem) {
        try {
          console.log('Linking ApprovedItem to expense...');
          const linkResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EXPENSES.UPDATE(expenseId)}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              data: {
                ApprovedItem: formData.selectedApprovedItem
              }
            })
          });

          if (!linkResponse.ok) {
            const errorData = await linkResponse.json();
            console.error('Failed to link ApprovedItem:', errorData);
            // Continue - attachment linking is more critical
          } else {
            console.log('✓ ApprovedItem linked successfully');
          }
        } catch (error) {
          console.error('Error linking ApprovedItem:', error);
          // Continue - not critical
        }
      }

      // Step 2: Upload files if any (upload once, track IDs properly)
      if (uploadedFiles.length > 0) {
        console.log('Starting file upload for', uploadedFiles.length, 'files...');
        
        // Upload all files in parallel (once each, no retries during upload)
        const uploadPromises = uploadedFiles.map(async (fileArtifact) => {
          if (!fileArtifact.file) {
            console.warn('No file attached to artifact:', fileArtifact.name);
            return { fileName: fileArtifact.name, success: false, ids: [] };
          }

          try {
            setUploadProgress(prev => ({
              ...prev,
              [fileArtifact.name]: 'Uploading...'
            }));

            const formDataUpload = new FormData();
            formDataUpload.append('files', fileArtifact.file);

            console.log('Uploading file:', fileArtifact.name);

            const uploadResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD.FILE}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
              body: formDataUpload
            });

            if (!uploadResponse.ok) {
              const errorMsg = `HTTP ${uploadResponse.status}`;
              console.warn(`Upload failed for ${fileArtifact.name}:`, errorMsg);
              
              setUploadProgress(prev => ({
                ...prev,
                [fileArtifact.name]: '⚠ Upload failed'
              }));
              
              return { fileName: fileArtifact.name, success: false, ids: [] };
            }

            const uploadData = await uploadResponse.json();
            console.log(`✓ Upload successful for ${fileArtifact.name}:`, uploadData);
            
            // Extract file IDs from response
            let fileIds: any[] = [];
            if (Array.isArray(uploadData)) {
              fileIds = uploadData;
            } else if (uploadData && typeof uploadData === 'object') {
              fileIds = [uploadData];
            }

            console.log(`Extracted ${fileIds.length} ID(s) from response:`, fileIds);

            setUploadProgress(prev => ({
              ...prev,
              [fileArtifact.name]: '✓ Uploaded'
            }));

            return { fileName: fileArtifact.name, success: true, ids: fileIds };
          } catch (error) {
            console.error(`Error uploading ${fileArtifact.name}:`, error);
            
            setUploadProgress(prev => ({
              ...prev,
              [fileArtifact.name]: '✗ Upload error'
            }));
            
            return { fileName: fileArtifact.name, success: false, ids: [] };
          }
        });

        // Wait for all uploads to complete
        const uploadResults = await Promise.all(uploadPromises);
        console.log('All uploads completed. Results:', uploadResults);

        // Collect file IDs from successful uploads
        uploadResults.forEach(result => {
          if (result.success && result.ids.length > 0) {
            console.log(`Adding ${result.ids.length} file ID(s) from ${result.fileName}`);
            uploadedFileIds = [...uploadedFileIds, ...result.ids];
          }
        });

        console.log('Total file IDs collected for attachment:', uploadedFileIds.length, uploadedFileIds);
      }

      // Step 3: Attach uploaded files to expense (single attempt)
      if (uploadedFileIds.length > 0) {
        try {
          console.log('Attaching', uploadedFileIds.length, 'file(s) to expense...');
          
          // Wait a moment to ensure files are fully persisted
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Extract valid file IDs
          const fileIds = uploadedFileIds
            .map((file: any) => file.id || (typeof file === 'string' ? file : null))
            .filter(Boolean);
          
          if (fileIds.length === 0) {
            console.warn('No valid file IDs to attach');
          } else {
            console.log('File IDs to attach:', fileIds);

            const updateFileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EXPENSES.UPDATE(expenseId)}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                data: {
                  attachment: fileIds,
                }
              })
            });

            if (updateFileResponse.ok) {
              await updateFileResponse.json();
              console.log('✓ Files attached successfully');
            } else {
              const errorData = await updateFileResponse.json();
              console.warn('⚠ Failed to attach files, but expense is created. Files are saved to disk.', errorData);
            }
          }
        } catch (attachError) {
          console.warn('⚠ Error attaching files, but expense is created. Files are saved to disk.', attachError);
        }
      }

      // Step 4: Publish the expense
      // NOTE: draftAndPublish is disabled in backend schema, so publishedAt is auto-managed
      // Skip this step to avoid overwriting other fields
      console.log('Skipping publish step (draftAndPublish disabled in schema)');

      // Step 5: Update itemRequest remainingAmount and set budgetWithinApprovalLevel
      if (formData.selectedApprovedItem) {
        try {
          const selectedItem = approvedItems.find(item => item.documentId === formData.selectedApprovedItem);
          
          if (selectedItem) {
            const requestAmount = totalAmount; // Use calculated totalAmount from expenseItems, not deprecated formData.totalPrice
            const approvedAmount = selectedItem.totalPrice || 0;
            const currentRemainingAmount = selectedItem.remianingAmount;
            
            // Calculate new remaining amount based on whether remianingAmount already exists
            let newRemainingAmount;
            if (currentRemainingAmount === null || currentRemainingAmount === undefined) {
              // First request: remaining = ApprovedAmount - CurrentRequestPrice
              newRemainingAmount = approvedAmount - requestAmount;
              console.log('First request - calculating from approved amount:', {
                approvedAmount: approvedAmount,
                requestAmount: requestAmount,
                newRemainingAmount: newRemainingAmount
              });
            } else {
              // Subsequent requests: remaining = currentRemainingAmount - CurrentRequestPrice
              newRemainingAmount = currentRemainingAmount - requestAmount;
              console.log('Subsequent request - subtracting from remaining amount:', {
                currentRemainingAmount: currentRemainingAmount,
                requestAmount: requestAmount,
                newRemainingAmount: newRemainingAmount
              });
            }

            // Calculate budgetWithinApprovalLevel based on remaining amount vs approved amount
            // totalSpentAfterThisExpense = approvedAmount - newRemainingAmount
            const totalSpentAfterThisExpense = approvedAmount - newRemainingAmount;
            let budgetWithinApprovalLevel: string;

            if (totalSpentAfterThisExpense <= approvedAmount) {
              // Within approved budget
              budgetWithinApprovalLevel = 'withinApprovalLevel';
              console.log('Budget Level: withinApprovalLevel', {
                totalSpent: totalSpentAfterThisExpense,
                approved: approvedAmount
              });
            } else if (totalSpentAfterThisExpense <= approvedAmount * 1.2) {
              // Over approved but within +20% threshold
              budgetWithinApprovalLevel = 'aboveApprovalLevelButWithinThreshold';
              console.log('Budget Level: aboveApprovalLevelButWithinThreshold', {
                totalSpent: totalSpentAfterThisExpense,
                approved: approvedAmount,
                threshold: approvedAmount * 1.2
              });
            } else {
              // Over +20% threshold
              budgetWithinApprovalLevel = 'aboveApprovalLevel';
              console.log('Budget Level: aboveApprovalLevel', {
                totalSpent: totalSpentAfterThisExpense,
                approved: approvedAmount,
                threshold: approvedAmount * 1.2
              });
            }

            // Update itemRequest with new remaining amount
            const updateItemResponse = await fetch(
              `${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.UPDATE(formData.selectedApprovedItem)}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  data: {
                    remianingAmount: newRemainingAmount,
                  }
                })
              }
            );

            if (updateItemResponse.ok) {
              console.log('✓ ItemRequest remaining amount updated successfully');
            } else {
              const errorData = await updateItemResponse.json();
              console.warn('⚠ Failed to update itemRequest remaining amount:', errorData);
            }

            // Update expense with budgetWithinApprovalLevel
            const updateExpenseResponse = await fetch(
              `${API_BASE_URL}${API_ENDPOINTS.EXPENSES.UPDATE(expenseId)}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  data: {
                    budgetWithinApprovalLevel: budgetWithinApprovalLevel,
                  }
                })
              }
            );

            if (updateExpenseResponse.ok) {
              console.log('✓ Expense budgetWithinApprovalLevel updated successfully:', budgetWithinApprovalLevel);
            } else {
              const errorData = await updateExpenseResponse.json();
              console.warn('⚠ Failed to update expense budgetWithinApprovalLevel:', errorData);
            }
          }
        } catch (updateError) {
          console.warn('⚠ Error in Step 5 updates:', updateError);
        }
      }

      const selectedItem = approvedItems.find(item => item.documentId === formData.selectedApprovedItem);

      const newExpense: ExpenseItem = {
        id: expenseId,
        title: formData.title,
        expenseItems: expenseItems,
        totalAmount: totalAmount,
        isExceptionRequest: formData.isExceptionRequest === 'Yes',
        sendExceptionApprovalTo: formData.exceptionApprovalTo,
        approvedItem: selectedItem,
        attachments: uploadedFiles,
        createdAt: new Date().toISOString(),
        comment: formData.comment || undefined,
      };

      setSubmittedExpenses([...submittedExpenses, newExpense]);
      setSubmitSuccess(true);

      // Reset form
      setFormData({
        title: '',
        selectedApprovedItem: '',
        isExceptionRequest: 'No',
        exceptionApprovalTo: '',
        comment: '',
        unitPrice: '',
        totalPrice: '',
      });
      setExpenseItems([]);
      setUploadedFiles([]);
      setUploadProgress({});
      setUploadProgress({});

      // Switch to summary tab
      setTimeout(() => {
        setTabValue(0);
        setSubmitSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting expense:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 3 }, justifyContent: 'space-between' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }, flex: '0 0 auto' }}>
            Expense Report
          </Typography>
          {!isProcurement && (
            <Alert severity="warning" sx={{ mb: 0, flex: { xs: '0 0 100%', md: '0 0 auto' } }}>
              Only users with Procurement role can submit expense reports.
            </Alert>
          )}
        </Box>

        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Expense submitted successfully!
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="expense report tabs"
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                fontWeight: 600,
                minHeight: { xs: '48px', sm: '56px' },
                textTransform: 'none',
                padding: { xs: '8px 12px', sm: '12px 16px' },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: '3px',
              },
            }}
          >
            <Tab label="Expense Summary" id="expense-tab-0" aria-controls="expense-tabpanel-0" />
            <Tab label="Expense Details" id="expense-tab-1" aria-controls="expense-tabpanel-1" />
            <Tab 
              label="Submit Expense Report" 
              id="expense-tab-2" 
              aria-controls="expense-tabpanel-2"
              disabled={!isProcurement}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            {expenseLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <LinearProgress sx={{ width: '100%', mb: 2 }} />
                <Typography color="textSecondary">Loading expenses...</Typography>
              </Box>
            ) : (
              <>
                {/* ═══════════════════════════════════════════════════════ */}
                {/* SECTION 1 — EXPENSE OVERVIEW DASHBOARD                  */}
                {/* ═══════════════════════════════════════════════════════ */}
                {(() => {
                  const totalSpent = submittedExpenses.reduce(
                    (sum, e) => sum + getExpenseTotalAmount(e), 0
                  );
                  // Deduplicate approved items by documentId so budget isn't double-counted
                  const seenAI = new Set<string>();
                  let totalApprovedBudget = 0;
                  submittedExpenses.forEach(e => {
                    const ai = (e as any).ApprovedItem || {};
                    const docId = ai.documentId;
                    if (docId && !seenAI.has(docId)) {
                      seenAI.add(docId);
                      totalApprovedBudget += ai.totalPrice || 0;
                    }
                  });

                  const exceptionExpenses = submittedExpenses.filter(e => e.isExceptionRequest);
                  const exceptionTotal = exceptionExpenses.reduce(
                    (sum, e) => sum + getExpenseTotalAmount(e), 0
                  );

                  // Group by approval request category, deduplicating approved budget per item
                  const catMap: Record<string, {
                    spent: number; approvedBudget: number;
                    requestId?: string; seenItems: Set<string>;
                  }> = {};
                  submittedExpenses.forEach(e => {
                    const ai = (e as any).ApprovedItem || {};
                    const ar = ai.aproval_request || {};
                    const cat = ar.title || (e.isExceptionRequest ? 'Exception / Uncategorized' : 'Uncategorized');
                    const reqId = ar.documentId || ar.id;
                    const aiDocId = ai.documentId;
                    if (!catMap[cat]) {
                      catMap[cat] = { spent: 0, approvedBudget: 0, requestId: reqId, seenItems: new Set() };
                    }
                    catMap[cat].spent += getExpenseTotalAmount(e);
                    if (aiDocId && !catMap[cat].seenItems.has(aiDocId)) {
                      catMap[cat].seenItems.add(aiDocId);
                      catMap[cat].approvedBudget += ai.totalPrice || 0;
                    }
                  });
                  const categories = Object.entries(catMap).map(([cat, d]) => ({
                    cat, spent: d.spent, approvedBudget: d.approvedBudget, requestId: d.requestId,
                  }));

                  // Top 3 expense categories by individual item (item_estimate)
                  const itemCatMap: Record<string, { spent: number; approvedBudget: number; seenItems: Set<string> }> = {};
                  submittedExpenses.forEach(e => {
                    const ai = (e as any).ApprovedItem || {};
                    const ar = ai.aproval_request || {};
                    const ie = ai.item_estimate || {};
                    const cat = ie.item || ar.title || (e.isExceptionRequest ? 'Exception / Uncategorized' : 'Uncategorized');
                    const aiDocId = ai.documentId;
                    if (!itemCatMap[cat]) {
                      itemCatMap[cat] = { spent: 0, approvedBudget: 0, seenItems: new Set() };
                    }
                    itemCatMap[cat].spent += getExpenseTotalAmount(e);
                    if (aiDocId && !itemCatMap[cat].seenItems.has(aiDocId)) {
                      itemCatMap[cat].seenItems.add(aiDocId);
                      itemCatMap[cat].approvedBudget += ai.totalPrice || 0;
                    }
                  });
                  const top3Categories = Object.entries(itemCatMap)
                    .map(([cat, d]) => ({ cat, spent: d.spent, approvedBudget: d.approvedBudget }))
                    .sort((a, b) => b.spent - a.spent)
                    .slice(0, 3);

                  // Card 1 primary: vs total commitment paid
                  const commPct = totalCommitmentPaid > 0
                    ? Math.min((totalSpent / totalCommitmentPaid) * 100, 100) : 0;
                  // Card 1 footer: vs approved budget
                  const approvedPct = totalApprovedBudget > 0
                    ? Math.min((totalSpent / totalApprovedBudget) * 100, 100) : 0;

                  return (
                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={2} alignItems="stretch">

                        {/* ── Card a: Total Expenses vs Commitment Paid ── */}
                        <Grid item xs={12} md={4}>
                          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #1976d2', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', letterSpacing: '0.6px', mb: 1 }}>
                              Total expenses VS Total received amount(commitment and other sources)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1.5 }}>
                              <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#1976d2', lineHeight: 1 }}>
                                ₹{totalSpent.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </Typography>
                              <Typography sx={{ fontSize: '0.9rem', color: '#aaa' }}>
                                &nbsp;/&nbsp;₹{(commitmentLoading ? '…' : totalCommitmentPaid.toLocaleString('en-IN', { maximumFractionDigits: 0 }))}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant={commitmentLoading ? 'indeterminate' : 'determinate'}
                              value={commPct}
                              sx={{
                                height: 12, borderRadius: 6,
                                backgroundColor: '#e3f2fd',
                                mb: 0.75,
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 6,
                                  backgroundColor: commPct >= 100 ? '#d32f2f' : commPct >= 80 ? '#f57c00' : '#1976d2',
                                },
                              }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                              <Typography sx={{ fontSize: '0.68rem', color: '#aaa' }}>0%</Typography>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: commPct >= 100 ? '#d32f2f' : commPct >= 80 ? '#f57c00' : '#1976d2' }}>
                                {commitmentLoading ? '…' : `${commPct.toFixed(1)}% of collected`}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: '#aaa' }}>100%</Typography>
                            </Box>
                            {/* Main footer row: remaining from collection */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                              <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>Remaining (of collected):</Typography>
                              <Typography sx={{
                                fontSize: '0.85rem', fontWeight: 700,
                                color: totalCommitmentPaid - totalSpent < 0 ? '#d32f2f' : '#388e3c'
                              }}>
                                {commitmentLoading ? '…' : `₹${(totalCommitmentPaid - totalSpent).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                              </Typography>
                            </Box>
                            {/* Secondary footer: vs approved budget */}
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                                vs Approved Budget
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={approvedPct}
                                  sx={{
                                    flex: 1, height: 5, borderRadius: 3,
                                    backgroundColor: '#f5f5f5',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 3,
                                      backgroundColor: approvedPct >= 100 ? '#d32f2f' : approvedPct >= 80 ? '#f57c00' : '#5c85d6',
                                    },
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: approvedPct >= 100 ? '#d32f2f' : approvedPct >= 80 ? '#f57c00' : '#5c85d6', whiteSpace: 'nowrap' }}>
                                  {approvedPct.toFixed(0)}%
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>
                                  ₹{totalSpent.toLocaleString('en-IN', { maximumFractionDigits: 0 })} spent of ₹{totalApprovedBudget.toLocaleString('en-IN', { maximumFractionDigits: 0 })} approved
                                </Typography>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: totalApprovedBudget - totalSpent < 0 ? '#d32f2f' : '#388e3c' }}>
                                  {totalApprovedBudget - totalSpent < 0 ? '▲' : '▼'} ₹{Math.abs(totalApprovedBudget - totalSpent).toLocaleString('en-IN', { maximumFractionDigits: 0 })} {totalApprovedBudget - totalSpent < 0 ? 'over' : 'left'}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>

                        {/* ── Card b: Top 3 Expense Categories ── */}
                        <Grid item xs={12} md={4}>
                          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #7b1fa2', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1 }}>
                              Top 3 Expense Categories
                            </Typography>
                            {top3Categories.length === 0 ? (
                              <Typography sx={{ fontSize: '0.78rem', color: '#bbb' }}>No expenses submitted yet</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                                {top3Categories.map((tc, idx) => {
                                  const pct = tc.approvedBudget > 0 ? (tc.spent / tc.approvedBudget) * 100 : 0;
                                  const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                                  return (
                                    <Box key={tc.cat} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
                                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                          {tc.cat}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#7b1fa2', whiteSpace: 'nowrap' }}>
                                          ₹{tc.spent.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ flex: 1, height: 5, borderRadius: 3, backgroundColor: '#f0f0f0', overflow: 'hidden' }}>
                                          <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 3, backgroundColor: pct >= 100 ? '#d32f2f' : pct >= 80 ? '#f57c00' : '#7b1fa2' }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '0.62rem', color: '#aaa', minWidth: 28, textAlign: 'right' }}>
                                          {pct.toFixed(0)}%
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '0.62rem', color: '#aaa' }}>
                                          Budget: ₹{tc.approvedBudget.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Paper>
                        </Grid>

                        {/* ── Card c: Spend by Approval Category ── */}
                        <Grid item xs={12} md={4}>
                          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #388e3c', height: '100%', boxSizing: 'border-box' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.5 }}>
                              Spend by Approval Category
                            </Typography>
                            {categories.length === 0 ? (
                              <Typography sx={{ fontSize: '0.78rem', color: '#bbb' }}>No data available</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                                {categories.map(({ cat, spent, approvedBudget, requestId }) => {
                                  const pct = approvedBudget > 0 ? Math.min((spent / approvedBudget) * 100, 100) : 0;
                                  const overBudget = approvedBudget > 0 && spent > approvedBudget;
                                  return (
                                    <Box key={cat}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                        <Typography
                                          component={requestId ? 'a' : 'span'}
                                          href={requestId ? `/approval/${requestId}` : undefined}
                                          sx={{
                                            fontSize: '0.75rem', fontWeight: 600,
                                            color: requestId ? '#1976d2' : '#444',
                                            textDecoration: 'none',
                                            maxWidth: '65%', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            display: 'block',
                                            '&:hover': requestId ? { textDecoration: 'underline' } : {},
                                          }}
                                        >
                                          {cat}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: overBudget ? '#d32f2f' : '#333' }}>
                                          ₹{spent.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={pct}
                                        sx={{
                                          height: 7, borderRadius: 4,
                                          backgroundColor: '#e8f5e9',
                                          '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            backgroundColor: pct >= 100 ? '#d32f2f' : pct >= 80 ? '#f57c00' : '#388e3c',
                                          },
                                        }}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.4 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>
                                          Budget: ₹{approvedBudget.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: pct >= 100 ? '#d32f2f' : pct >= 80 ? '#f57c00' : '#388e3c' }}>
                                          {pct.toFixed(0)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Paper>
                        </Grid>

                        {/* ── Card d: Exception Requests ── */}
                        <Grid item xs={12} md={4}>
                          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #f57c00', height: '100%', boxSizing: 'border-box' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1 }}>
                              Exception Requests
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                              <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#f57c00', lineHeight: 1 }}>
                                {exceptionExpenses.length}
                              </Typography>
                              <Typography sx={{ fontSize: '0.85rem', color: '#aaa' }}>
                                &nbsp;out of {submittedExpenses.length} expense{submittedExpenses.length !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.82rem', color: '#666', mb: 1.5 }}>
                              Total exception amount:{' '}
                              <strong style={{ color: '#f57c00' }}>
                                ₹{exceptionTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </strong>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                              {exceptionExpenses.length > 0 ? (
                                <>
                                  <Chip
                                    label={`${exceptionExpenses.filter(e => (e as any).ExceptionApproval?.status === 'Approved').length} Approved`}
                                    size="small" color="success" variant="outlined"
                                  />
                                  <Chip
                                    label={`${exceptionExpenses.filter(e => {
                                      const s = (e as any).ExceptionApproval?.status;
                                      return !s || s === 'Pending';
                                    }).length} Pending`}
                                    size="small" variant="outlined"
                                  />
                                  <Chip
                                    label={`${exceptionExpenses.filter(e => (e as any).ExceptionApproval?.status === 'Rejected').length} Rejected`}
                                    size="small" color="error" variant="outlined"
                                  />
                                </>
                              ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#bbb' }}>No exception requests yet</Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>

                      </Grid>
                    </Box>
                  );
                })()}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box>
            {expenseLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <LinearProgress sx={{ width: '100%', mb: 2 }} />
                <Typography color="textSecondary">Loading expenses...</Typography>
              </Box>
            ) : (
              <>
                {/* Nested Tabs for Expense Details */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={detailsTabValue}
                    onChange={(_, newValue) => setDetailsTabValue(newValue)}
                    aria-label="expense details view tabs"
                    sx={{
                      '& .MuiTab-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.95rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        padding: { xs: '8px 12px', sm: '12px 16px' },
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#1976d2',
                        height: '3px',
                      },
                    }}
                  >
                    <Tab label="By Category" id="details-tab-0" aria-controls="details-tabpanel-0" />
                    <Tab label="Individual Expenses" id="details-tab-1" aria-controls="details-tabpanel-1" />
                  </Tabs>
                </Box>

                {/* ═══════════════════════════════════════════════════════ */}
                {/* NESTED TAB 1 — INDIVIDUAL EXPENSES                     */}
                {/* ═══════════════════════════════════════════════════════ */}
                {detailsTabValue === 1 && (
                  <IndividualExpenses
                    submittedExpenses={submittedExpenses}
                    downloadingSpreadsheet={downloadingSpreadsheet}
                    onDownloadSpreadsheet={handleDownloadExpensesSpreadsheet}
                  />
                )}


                {/* ═══════════════════════════════════════════════════════ */}
                {/* NESTED TAB 0 — EXPENSES BY CATEGORY (GROUPED)          */}
                {/* ═══════════════════════════════════════════════════════ */}
                {detailsTabValue === 0 && (
                  <ByCategoryExpenses
                    submittedExpenses={submittedExpenses}
                    downloadingSpreadsheet={downloadingSpreadsheet}
                    onDownloadSpreadsheet={handleDownloadExpensesSpreadsheet}
                  />
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 600 }}>
              Submit Expense Report
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title of Expense"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Enter expense title"
                  size="small"
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </Grid>

              {/* Two side-by-side sections: Exception Request and Pre-Approved Item Category */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Left Side: Exception Request */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                          Is This a POST APPROVAL Exception Request?
                        </Typography>
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <RadioGroup
                            row
                            name="isExceptionRequest"
                            value={formData.isExceptionRequest}
                            onChange={handleSelectChange}
                          >
                            <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          </RadioGroup>
                        </FormControl>

                        {formData.isExceptionRequest === 'Yes' && (
                          <FormControl fullWidth size="small">
                            <InputLabel>Use exception approval of</InputLabel>
                            <Select
                              name="exceptionApprovalTo"
                              value={formData.exceptionApprovalTo}
                              onChange={handleSelectChange}
                              label="Use exception approval of"
                            >
                              <MenuItem value="President [Brajesh Charan Ghosh (Laddu)]">
                                <Box sx={{ py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>President [Brajesh Charan Ghosh (Laddu)]</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                    <p style={{ margin: '4px 0' }}>Cumulative 2% approval (2% of 50L = 100,000 INR)</p>
                                    <p style={{ margin: '4px 0' }}>Max 10k per item | Emergency expenses only</p>
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem value="Secretary [Ashish Ghosh]">
                                <Box sx={{ py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Secretary [Ashish Ghosh]</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                    <p style={{ margin: '4px 0' }}>Cumulative 2% approval (2% of 50L = 100,000 INR)</p>
                                    <p style={{ margin: '4px 0' }}>Max 10k per item | Emergency expenses only</p>
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem value="Organizer/Treasurer (Karpardaar)">
                                <Box sx={{ py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Organizer/Treasurer (Karpardaar)</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                    <p style={{ margin: '4px 0' }}>Cumulative 1% approval (1% of 50L = 50,000 INR)</p>
                                    <p style={{ margin: '4px 0' }}>Max 10k per item | Emergency expenses only</p>
                                  </Typography>
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {formData.isExceptionRequest === 'Yes' && (
                          <Box sx={{ mt: 2, p: 1, backgroundColor: getGrandTotal() > 10000 ? '#ffebee' : '#e3f2fd', borderRadius: 1, border: `1px solid ${getGrandTotal() > 10000 ? '#ef5350' : '#90caf9'}` }}>
                            <Typography variant="caption" sx={{ color: getGrandTotal() > 10000 ? '#d32f2f' : '#1976d2', fontWeight: 500 }}>
                              {getGrandTotal() > 10000 ? '⚠️ Total exceeds ₹10,000 limit for exception requests' : '💡 Exception request limit: ₹10,000 maximum total'}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Right Side: Pre-Approved Item Category */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        backgroundColor: (() => {
                          if (!budgetStatus) return 'transparent';
                          switch (budgetStatus) {
                            case 'within':
                              return '#c8e6c9'; // Green
                            case 'threshold':
                              return '#ffe0b2'; // Orange
                            case 'above':
                              return '#ffcdd2'; // Red
                            default:
                              return 'transparent';
                          }
                        })(),
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                          Pre-Approved Item Category
                          {formData.isExceptionRequest === 'No' && <span style={{ color: '#d32f2f' }}>*</span>}
                          {formData.isExceptionRequest === 'Yes' && <span style={{ color: '#999', fontSize: '0.8em' }}> (Optional for Exception Request)</span>}
                        </Typography>
                        {formData.isExceptionRequest === 'Yes' && (
                          <Box sx={{ mb: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                            <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500 }}>
                              💡 You can submit this exception request without selecting an approved item. It will be routed for special approval.
                            </Typography>
                          </Box>
                        )}
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Approved Item {formData.isExceptionRequest === 'Yes' ? '(Optional)' : ''}</InputLabel>
                          <Select
                            name="selectedApprovedItem"
                            value={formData.selectedApprovedItem}
                            onChange={handleSelectChange}
                            label={`Select Approved Item ${formData.isExceptionRequest === 'Yes' ? '(Optional)' : ''}`}
                          >
                            {approvedItems.length === 0 ? (
                              <MenuItem disabled>No approved items available</MenuItem>
                            ) : (
                              (() => {
                                // Sort items: available (positive remaining) first, then unavailable (negative remaining)
                                const availableItems = approvedItems.filter(item => {
                                  const remaining = item.remianingAmount !== null && item.remianingAmount !== undefined 
                                    ? item.remianingAmount 
                                    : item.totalPrice;
                                  return remaining >= 0;
                                });
                                const unavailableItems = approvedItems.filter(item => {
                                  const remaining = item.remianingAmount !== null && item.remianingAmount !== undefined 
                                    ? item.remianingAmount 
                                    : item.totalPrice;
                                  return remaining < 0;
                                });

                                return [
                                  ...availableItems.map((item) => (
                                    <MenuItem key={item.documentId} value={item.documentId}>
                                      {item.item_estimate?.item || 'N/A'} - ₹{item.totalPrice} (Approval: {item.aproval_request?.title || 'N/A'})
                                    </MenuItem>
                                  )),
                                  ...unavailableItems.map((item) => {
                                    const remaining = item.remianingAmount !== null && item.remianingAmount !== undefined 
                                      ? item.remianingAmount 
                                      : item.totalPrice;
                                    // Allow unavailable items to be selected if this is a POST APPROVAL exception request
                                    const isDisabled = formData.isExceptionRequest !== 'Yes';
                                    
                                    return (
                                      <MenuItem key={item.documentId} value={item.documentId} disabled={isDisabled}>
                                        <Box sx={{ width: '100%' }}>
                                          <Typography variant="body2">
                                            {item.item_estimate?.item || 'N/A'} - ₹{item.totalPrice} (Remaining: ₹{remaining.toFixed(2)})
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: formData.isExceptionRequest === 'Yes' ? '#4caf50' : '#d32f2f', fontWeight: 500 }}>
                                            {formData.isExceptionRequest === 'Yes' ? '✓ Exception Allowed' : '✗ Unavailable'}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    );
                                  })
                                ];
                              })()
                            )}
                          </Select>
                        </FormControl>
                        {formData.selectedApprovedItem && approvedItems.find(i => i.documentId === formData.selectedApprovedItem) && (
                          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f0f7ff', borderRadius: 1, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>Category:</strong> {approvedItems.find(i => i.documentId === formData.selectedApprovedItem)?.item_estimate?.item}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>Approved Amount:</strong> ₹{(approvedItems.find(i => i.documentId === formData.selectedApprovedItem)?.totalPrice || 0).toFixed(2)}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                mb: 0.5,
                                color: (() => {
                                  const selectedItem = approvedItems.find(i => i.documentId === formData.selectedApprovedItem);
                                  const remaining = selectedItem?.remianingAmount;
                                  return remaining !== null && remaining !== undefined && remaining < 0 ? '#d32f2f' : '#388e3c';
                                })(),
                                fontWeight: 600
                              }}
                            >
                              <strong>Remaining Balance:</strong> ₹{(() => {
                                const selectedItem = approvedItems.find(i => i.documentId === formData.selectedApprovedItem);
                                const remaining = selectedItem?.remianingAmount;
                                if (remaining === null || remaining === undefined) {
                                  return (selectedItem?.totalPrice || 0).toFixed(2);
                                }
                                return remaining.toFixed(2);
                              })()}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              <strong>Approval Title:</strong> {approvedItems.find(i => i.documentId === formData.selectedApprovedItem)?.aproval_request?.title}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
              </Grid>

              {/* Expense Items Table */}
              <Grid item xs={12}>
                <ExpenseItemsTable
                  items={expenseItems}
                  onItemsChange={setExpenseItems}
                  isExceptionRequest={formData.isExceptionRequest === 'Yes'}
                />
              </Grid>

              {/* File Upload */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                      Receipt/Quotes
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                    >
                      Upload Files
                      <input type="file" hidden multiple onChange={handleFileUpload} />
                    </Button>

                    {uploadedFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                          Uploaded Files ({uploadedFiles.length}):
                        </Typography>
                        {uploadedFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              fontSize: { xs: '0.65rem', md: '0.75rem' },
                            }}
                          >
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleRemoveFile(index)}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Comments Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                      Comments (Optional)
                    </Typography>
                    <RichTextEditor
                      value={formData.comment}
                      onChange={(content) => setFormData({ ...formData, comment: content })}
                      label="Add formatted comments with text styling"
                      placeholder="Enter your comments here..."
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmitExpense}
              disabled={isSubmitting || (formData.isExceptionRequest === 'Yes' && getGrandTotal() > 10000)}
              sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Expense Report'}
            </Button>
            </Grid>
          </Paper>
        </TabPanel>
      </Container>

      {/* Upload Progress Dialog */}
      <Dialog open={isSubmitting} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ mb: 2, fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Uploading Files...
          </Typography>
          <LinearProgress sx={{ mb: 2 }} />
          {Object.entries(uploadProgress).map(([fileName, status]) => (
            <Typography key={fileName} variant="caption" sx={{ display: 'block', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
              {fileName}: {status}
            </Typography>
          ))}
        </Box>
      </Dialog>

      {/* Image Lightbox Modal */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ backgroundColor: '#000', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full view"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>    </Box>
  );
}